import { Fragment, createElement } from "react";
import HourglassGlobals from "./globals";
import { CollatorSingleton } from "./locale";
import { XSRFHeader } from "../api/const";
import { firstMondayOfMonth } from "./dateHelpers";

//we use this to try to keep heights consistent in a few places
//just rendering an empty string or a space doesn't cause the browser to end up with the same height as when
//there's actual text, but this seems to.
export const NBSP = "\u00A0";
const guidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
export const MaxSongNumber = 164;

export function shallowEqualObjects<T extends object>(a: T | undefined, b: T | undefined): boolean {
  if ((a && !b) || (b && !a)) {
    return false;
  }

  if (a && b) {
    if (Object.keys(a).length !== Object.keys(b).length) {
      return false;
    }

    for (const key in a) {
      if (a[key] !== b[key]) {
        return false;
      }
    }
  }

  return true;
}

export function getCookie(name: string): string {
  try {
    const nameVal = document.cookie.split("; ").find((row) => row.startsWith(name + "="));
    if (!nameVal) return "";
    // @ts-ignore noUncheckedIndexedAccess
    return nameVal.split("=", 2)[1];
  } catch (_e) {
    return "";
  }
}

export function haveCookie(name: string): boolean {
  return !!getCookie(name);
}

export function supportsCookies(): boolean {
  if (!navigator.cookieEnabled) return false;

  try {
    // Create cookie
    const testCookie = "cookietest=1; SameSite=strict";
    document.cookie = testCookie;
    const ret = document.cookie.indexOf("cookietest=") !== -1;
    // Delete cookie
    document.cookie = testCookie + "; expires=Thu, 01-Jan-1970 00:00:01 GMT";
    return ret;
  } catch (_e) {
    return false;
  }
}

function clearCookie(name: string) {
  const exp = "=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";
  document.cookie = name + exp;
}

// it appears that in some cases, removing the XSRFHeader cookie isn't working. possibly strange browser behavior.
// we are setting a flag in session storage to indicate that the user signed out or is not authenticated
// we then won't try any kind of auto-login if this is set. it's cleared when the login page displays.
const signOutFlagKey = "hg_signOut";
const signOutFlagValue = "1";

function setSignOutFlag() {
  try {
    window.sessionStorage.setItem(signOutFlagKey, signOutFlagValue);
  } catch (_e) {
    // noop
  }
}

export function hasSignOutFlag(): boolean {
  try {
    return window.sessionStorage.getItem(signOutFlagKey) === signOutFlagValue;
  } catch (_e) {
    return false;
  }
}

export function clearSignOutFlag() {
  try {
    window.sessionStorage.removeItem(signOutFlagKey);
  } catch (_e) {
    //noop
  }
}

export function signOut(clearSub = true) {
  clearCookie(XSRFHeader);
  clearCookie("hglogin");
  setSignOutFlag();
  if (clearSub) {
    clearCookie(HourglassGlobals.GoogleSubCookie);
    clearCookie(HourglassGlobals.AppleSubCookie);
  }

  window.location.href = HourglassGlobals.AppBase;
}

//show 0, 1 or 2 decimal places as needed
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function stringCompare(a?: string, b?: string): number {
  if (!a || !b) return 0;
  return CollatorSingleton.getInstance().compare(a, b);
}

export const HourMs = 1000 * 60 * 60;

//from a long stackoverflow on the best way to chunk an array...
export function chunk<T>(arr: T[], size: number): T[][] {
  return [...Array(Math.ceil(arr.length / size))].map((_, i) => arr.slice(size * i, size + size * i));
}

//for when we want to distribute elements of an array evenly across x columns
export function chunkColumns<T>(arr: T[], columns: number): T[][] {
  const rows = Math.floor(arr.length / columns);
  const rem = arr.length % columns;
  return [...Array(columns)].map((_, i) => {
    const start = i * rows + Math.min(i, rem);
    return arr.slice(start, start + rows + (i < rem ? 1 : 0));
  });
}

export function defaultMidweekDate(): Date {
  return firstMondayOfMonth(new Date());
}

export function isValidHttpUrl(string: string): boolean {
  try {
    const newUrl = new URL(string);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (_e) {
    return false;
  }
}

// extract removes a specific element from an array
export function extract<T>(arr: T[], value: T) {
  const idx = arr.indexOf(value);
  if (idx > -1) {
    arr.splice(idx, 1);
  }
  return arr;
}

export async function sha256hash(string: string) {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest("SHA-256", utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((bytes) => bytes.toString(16).padStart(2, "0")).join("");
}

export function numberArrayRange(start: number, stop: number, step = 1): number[] {
  return Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + i * step);
}

export function awaitTimeout<T>(promise: Promise<T>, timeout: number, reason: string): Promise<T | void> {
  return Promise.race([
    promise,
    new Promise<void>((resolve, reject) =>
      setTimeout(() => (reason === undefined ? resolve() : reject(reason)), timeout),
    ),
  ]);
}

export function setsAreEqual<T>(a: Set<T>, b: Set<T>): boolean {
  return a.difference(b).size === 0 && a.size === b.size;
}

/**
 * @description
 * Takes an Array<V>, and a grouping function,
 * and returns a Map of the array grouped by the grouping function.
 *
 * @param list An array of type V.
 * @param keyGetter A Function that takes the Array type V as an input, and returns a value of type K.
 *                  K is generally intended to be a property key of V.
 *
 * @returns Map of the array grouped by the grouping function.
 */
export function groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
  const map = new Map<K, Array<V>>();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

// mapFieldToObject maps value of field to object
export function mapFieldToObject<T, K extends keyof T>(arr: T[], field: K): Map<T[K], T> {
  const resultMap = new Map<T[K], T>();

  for (const obj of arr) {
    const fieldValue = obj[field];
    resultMap.set(fieldValue, obj);
  }

  return resultMap;
}

// move the element at fromIndex to toIndex and return the new array
export const arrayMove = <T>(arr: T[], fromIndex: number, toIndex: number): T[] => {
  const newArr = [...arr];
  // @ts-ignore index type warning
  newArr.splice(toIndex, 0, newArr.splice(fromIndex, 1)[0]);
  return newArr;
};

export function isNonEmptyArray(item: any): boolean {
  if (!Array.isArray(item)) return false;
  return item.length !== 0;
}

export function decodeHtml(str: string): string {
  const item = document.createElement("textarea");
  item.innerHTML = str;
  return item.value;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function combineTrimOptionalStrings(...str: string[]): string | undefined {
  let combined: string = "";

  str.forEach((s) => {
    if (s) combined += s.trim();
  });

  return combined ? combined : undefined;
}

export function hubEventId(text: string): string | null {
  try {
    // First try parsing the whole string (handles case where text IS the URL)
    const directUrl = URL.parse(text);
    if (directUrl) {
      const eventId = directUrl.searchParams.get("eventId");
      if (eventId) {
        // Extract only the GUID portion from the eventId parameter
        const guidMatch = guidRegex.exec(eventId);
        if (guidMatch) return guidMatch[0];
      }
    }
  } catch {
    // Continue to regex approach
  }

  // Match URLs that start with http:// or https://
  // Stops at whitespace, quotes, or other common delimiters
  const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[]+/gi;
  const matches = text.match(urlRegex);

  if (!matches) return null;

  // Try each potential URL
  for (const match of matches) {
    try {
      const url = new URL(match);
      const eventId = url.searchParams.get("eventId");
      if (eventId) {
        // Extract only the GUID portion from the eventId parameter
        const guidMatch = guidRegex.exec(eventId);
        if (guidMatch) return guidMatch[0];
      }
    } catch {
      // Invalid URL, continue to next match
    }
  }

  return null;
}

export const getGUID = (inputText: string): string => {
  const hubGuid = hubEventId(inputText);
  if (hubGuid) return hubGuid.toUpperCase();
  const result = guidRegex.exec(inputText);
  if (Array.isArray(result) && result.length === 1) return result[0].toUpperCase();
  return "";
};

/**
 * iOS Safari's share sheet "Copy" action puts the shared text and the shared URL on the
 * pasteboard as two separate items. A plain-text paste into a <textarea> only reads the
 * first item (the text), so the URL is silently dropped. The URL is still on the
 * pasteboard under the text/uri-list flavor, so pull it out and merge it in ourselves.
 * @param clipboardData - the DataTransfer from a paste event
 * @returns the merged text + URL, or null when there is nothing extra to add (a normal paste)
 */
export const mergePastedShareURL = (clipboardData: DataTransfer | null): string | null => {
  if (!clipboardData) return null;
  const text = clipboardData.getData("text/plain");
  const url = clipboardData
    .getData("text/uri-list")
    .split(/\r?\n/)
    .find((line) => line && !line.startsWith("#"));
  if (!url || text.includes(url)) return null;
  return text ? `${text}\n${url}` : url;
};

const linkRegex = /(?:https?:\/\/|www\.)[^\s<>"{}|\\^`[]+/gi;

/**
 * Splits text on URLs and returns a React element with clickable links.
 * Non-URL portions are rendered as plain text. URLs open in a new tab.
 * @param text - The input string that may contain URLs
 * @returns A React element with linkified URLs, or the original string if no URLs are found
 */
export function linkifyText(text: string): React.ReactElement | string {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  linkRegex.lastIndex = 0;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    const href = url.startsWith("http") ? url : `https://${url}`;
    parts.push(createElement("a", { key: match.index, href, target: "_blank", rel: "noopener noreferrer" }, url));
    lastIndex = linkRegex.lastIndex;
  }

  if (parts.length === 0) return text;

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return createElement(Fragment, null, ...parts);
}
